import { useEffect, useState } from "react";
import type { TelegramWebApp } from "../types/telegram";

export const useTelegram = () => {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [safeAreaInset, setSafeAreaInset] = useState(0);

  useEffect(() => {
    if (typeof window !== "undefined" && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp as TelegramWebApp;
      setWebApp(tg);

      tg.ready();
      tg.expand();

      const safeArea = tg.safeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 };
      const contentSafeArea = tg.contentSafeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 };
      const totalTopInset = safeArea.top + contentSafeArea.top;
      const minHeight = 32;
      setSafeAreaInset(Math.max(totalTopInset, minHeight));

      setIsReady(true);
    }
  }, []);

  const sendData = (data: string) => {
    if (webApp) {
      webApp.sendData(data);
    }
  };

  const showAlert = (message: string) => {
    if (webApp) {
      webApp.showAlert(message);
    }
  };

  const showConfirm = (message: string, callback?: (confirmed: boolean) => void) => {
    if (webApp) {
      webApp.showConfirm(message, callback);
    }
  };

  const close = () => {
    if (webApp) {
      webApp.close();
    }
  };

  const hapticFeedback = {
    impact: (style: "light" | "medium" | "heavy" | "rigid" | "soft" = "medium") => {
      if (webApp?.HapticFeedback) {
        webApp.HapticFeedback.impactOccurred(style);
      }
    },
    notification: (type: "error" | "success" | "warning" = "success") => {
      if (webApp?.HapticFeedback) {
        webApp.HapticFeedback.notificationOccurred(type);
      }
    },
    selection: () => {
      if (webApp?.HapticFeedback) {
        webApp.HapticFeedback.selectionChanged();
      }
    },
  };

  return {
    webApp,
    isReady,
    user: webApp?.initDataUnsafe?.user,
    safeAreaInset,
    sendData,
    showAlert,
    showConfirm,
    close,
    hapticFeedback,
  };
};
